/**
 * DesktopNav - Desktop Navigation Component
 *
 * Clean desktop navigation with dropdowns and profile integration
 */
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigationStack } from '../../contexts/NavigationContext';
import './DesktopNav.css';

const DesktopNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { navigateWithTransition } = useNavigationStack();

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      // Navigate to home with scroll target in state
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      // Already on home page, scroll immediately
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="desktop-nav">
      <ul className="desktop-nav-links">
        <li>
          <Link
            to="/"
            className={isActive('/') ? 'active' : ''}
          >
            Home
          </Link>
        </li>
        <li>
          <button
            onClick={() => scrollToSection('trending-styles')}
            className="nav-link-button"
          >
            Trends
          </button>
        </li>
        <li>
          <Link
            to="/search"
            className={isActive('/search') ? 'active' : ''}
          >
            Search
          </Link>
        </li>
        <li>
          {currentUser ? (
            <Link
              to="/deals"
              className={isActive('/deals') ? 'active' : ''}
            >
              Deals
            </Link>
          ) : (
            <Link to="/auth" state={{ intendedAction: 'deals', message: 'Sign in to access exclusive deals and coupons' }}>Deals</Link>
          )}
        </li>
        <li>
          <Link
            to="/blog"
            className={isActive('/blog') ? 'active' : ''}
          >
            Blog
          </Link>
        </li>
        <li>
          {currentUser ? (
            <Link
              to="/favorites"
              className={isActive('/favorites') ? 'active' : ''}
            >
              Favorites
            </Link>
          ) : (
            <Link to="/auth">Favorites</Link>
          )}
        </li>
      </ul>

      <div className="desktop-nav-actions">
        <Link to="/search" className="icon-btn" aria-label="Search">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </Link>

        {currentUser ? (
          <Link to="/profile" className="profile-pic-btn" aria-label="Profile">
            {currentUser?.photoURL ? (
              <img
                src={currentUser.photoURL}
                alt="Profile"
                className="profile-pic"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="profile-pic-fallback" style={{ display: currentUser?.photoURL ? 'none' : 'flex' }}>
              {currentUser?.email?.charAt(0).toUpperCase() || '👤'}
            </div>
          </Link>
        ) : (
          <Link to="/auth" className="icon-btn" aria-label="Sign In">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </Link>
        )}
      </div>
    </nav>
  );
};

export default DesktopNav;

